import {useState, useEffect, useCallback, useRef} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faEye, faEyeSlash, faMinus, faPlus} from "@fortawesome/free-solid-svg-icons";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Utils
import {toTitleCase} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import BaseTable from "../../components/BaseTable.js";
import InputCheck from "../../components/form/InputCheck.js";
import Help from "../../components/Help.js";

// Style
import {heroTheme} from "../../style/components/variables.js";
import {
  Form,
  FormGroup,
  ButtonFull,
  Button,
  Inline,
  Heading,
  ButtonLoader,
  TableWrapper,
  TableHeaderWrap
} from "../../style/components/general.js";

const ModalUserAssign = ({
  visible,
  setVisible,
  hasBackButton,
  goBack,
  users,
  setUsers,
  filter,
  total
}) => {
  const isMounted = useMountedState();

  // Bulk mode assigns every user matching the table filter, adding facilities only
  const bulk = !!filter;
  const columnUsers = bulk ? [] : users;
  const bulkHeader = bulk ? (
    <Inline>
      Users will be added to all selected facilities
      <Help inverted title="Bulk assign">
        Existing assignments are kept. Unchecking a facility here will not remove users who are
        already assigned to it.
      </Help>
    </Inline>
  ) : null;

  const schema = yup.object().shape({
    facilities: yup.mixed().test({
      name: "required",
      message: "Please select at least one facility",
      test: val => Array.isArray(val) && val.length > 0
    })
  });

  const form = useForm({
    defaultValues: {facilities: []},
    resolver: yupResolver(schema)
  });
  const {setValue, handleSubmit, reset, watch} = form;

  const {api: apiFacilities} = useApi("facilities");
  const {api: apiFacilityUsers} = useApi("facility-users");

  const [showArchived, setShowArchived] = useState(false);
  const [facilities, setFacilities] = useState(undefined);
  const [userFacilities, setUserFacilities] = useState();
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkboxes = useRef();

  const watchFacilities = watch("facilities");

  const getFacilities = useCallback(
    (show = false) =>
      apiFacilities
        .callGet(null, {getAll: true, withDeleted: show, minified: true})
        .then(({data, status}) => {
          if (status === 200)
            setFacilities(
              data.map(facility => ({
                label: `${facility.name} ${toTitleCase(facility.type)}`,
                region: facility.region?.name?.toUpperCase(),
                value: facility.id,
                isDeleted: facility.isDeleted
              }))
            );
          setFacilityLoading(false);
        }),
    [apiFacilities]
  );

  const getFacilityUsers = useCallback(() => {
    apiFacilityUsers
      .callGet("", {ids: users.map(({publicId}) => publicId).join(",")})
      .then(({status, data}) => {
        if (status === 200 && data) {
          setUserFacilities(data);
          const intersection = [];
          Object.keys(data).map(publicId => {
            data[publicId].forEach(facility => {
              if (!intersection.includes(`${facility.id}`)) intersection.push(`${facility.id}`);
            });
          });
          setValue("facilities", intersection);
        }
      })
      .finally(() => setUserLoading(false));
  }, [apiFacilityUsers, users, setValue]);

  // Initial load - get all facilities
  useEffect(() => {
    if (isMounted()) {
      setFacilityLoading(true);
      getFacilities();
    }
  }, [isMounted, getFacilities]);

  useEffect(() => {
    if (isMounted() && !bulk && users.length > 0) {
      setUserLoading(true);
      getFacilityUsers();
    }
  }, [isMounted, bulk, users, getFacilityUsers]);

  const assignUsers = selected => {
    setLoading(true);
    const facilityIds = selected.facilities.map(id => parseInt(id, 10));
    apiFacilityUsers
      .callPost(
        bulk
          ? {filter, facilities: facilityIds, addOnly: true}
          : {
              users: users.map(user => user.email),
              facilities: facilityIds,
              preserveArchived: !showArchived
            }
      )
      .then(() => {
        setVisible(false);
        reset({});
        setUsers([]);
        setLoading(false);
      });
  };

  const handleSelectAll = () => {
    const inputs = checkboxes.current.querySelectorAll("input");
    const fieldValues = [];
    if (!watchFacilities || (watchFacilities && watchFacilities.length < facilities.length))
      Array.from(inputs).forEach(input => {
        fieldValues.push(input.value);
      });

    setValue("facilities", fieldValues);
  };

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      hasBackButton={hasBackButton}
      goBack={goBack}
      maxWidth="90vw">
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(assignUsers)} ref={checkboxes}>
          <TableWrapper>
            <TableHeaderWrap>
              <Heading>Assign Users</Heading>
              <Inline>
                {!facilityLoading && !userLoading && facilities !== undefined && (
                  <Button type="button" onClick={handleSelectAll}>
                    {watchFacilities?.length !== facilities?.length ? "Select All" : "Deselect All"}
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() => {
                    setFacilityLoading(true);
                    getFacilities(!showArchived);
                    setShowArchived(prev => !prev);
                  }}>
                  <FontAwesomeIcon icon={showArchived ? faEyeSlash : faEye} />
                  &nbsp;Archived
                </Button>
              </Inline>
            </TableHeaderWrap>
            <BaseTable
              headings={{
                member: {header: "Facility (Region)", disabled: true},
                ...(bulkHeader ? {bulkNote: {header: bulkHeader, disabled: true}} : {}),
                ...columnUsers.reduce((acc, {publicId, firstName, lastName}) => {
                  acc[publicId] = {header: `${firstName} ${lastName}`, disabled: true};
                  return acc;
                }, {})
              }}
              data={
                (bulk || userFacilities) && facilities?.length > 0
                  ? facilities.map(({label, region, value, isDeleted}) => ({
                      isDeleted,
                      member: (
                        <InputCheck name="facilities" value={value}>
                          {label} {region && `(${region})`} {isDeleted ? " (ARCHIVED)" : ""}
                        </InputCheck>
                      ),
                      ...(bulkHeader
                        ? {
                            bulkNote: <Icon icon={faPlus} color={heroTheme.success} />
                          }
                        : {}),
                      ...columnUsers.reduce((acc, {publicId}) => {
                        acc[publicId] = (
                          <Icon
                            icon={
                              Object.keys(userFacilities)?.includes(publicId) &&
                              userFacilities[publicId]?.find(
                                ({name, type}) =>
                                  `${name.toLowerCase()}${type ? ` ${type.toLowerCase()}` : ""}` ===
                                  label.toLowerCase()
                              )
                                ? faCheck
                                : faMinus
                            }
                            color={
                              Object.keys(userFacilities)?.includes(publicId) &&
                              userFacilities[publicId]?.find(
                                ({name, type}) =>
                                  `${name.toLowerCase()}${type ? ` ${type.toLowerCase()}` : ""}` ===
                                  label.toLowerCase()
                              )
                                ? heroTheme.success
                                : heroTheme.secondary
                            }
                          />
                        );
                        return acc;
                      }, {})
                    }))
                  : null
              }
              loading={facilityLoading || userLoading || facilities === undefined}
            />
          </TableWrapper>
          <FormGroup>
            <ButtonFull type="submit" loading={loading ? 1 : 0}>
              {bulk && `Add ${total} Users`}
              {!bulk && (users.length === 1 ? "Reassign" : "Assign")}
              {loading && <ButtonLoader />}
            </ButtonFull>
          </FormGroup>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalUserAssign.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  hasBackButton: PropTypes.bool,
  goBack: PropTypes.func,
  users: PropTypes.arrayOf(PropTypes.any).isRequired,
  setUsers: PropTypes.func.isRequired,
  filter: PropTypes.objectOf(PropTypes.any),
  total: PropTypes.number
};

// Style Overrides
const Icon = styled(FontAwesomeIcon)`
  fill: ${({color}) => color};
`;

export default ModalUserAssign;
